import { useMemo, useState } from 'react';
import { GitMerge } from 'lucide-react';
import { DataTable, EmptyState, StatusPill, Pagination, type ColumnDef } from '@vnetwork/ui/data';
import { Button } from '@vnetwork/ui/primitives';
import { Alert, ConfirmDialog } from '@vnetwork/ui/feedback';
import { Toolbar } from '@vnetwork/ui/layout';
import { TableLink } from '@/components/table-link';
import { EntityLink } from '@/components/entity-link';
import { paginationView } from '../pagination-summary';
import { useCustomerContracts, useMergeContracts, type ContractRow } from '@/api/customer-contracts';
import { formatDate, formatMoney } from '@/lib/format';
import { contractTone, humanize } from './status-tones';
import { TabError, TabLoading } from './tab-states';

function errMsg(err: unknown): string {
  const res = (err as { response?: { data?: { error_description?: string; error?: string } } })?.response;
  return res?.data?.error_description ?? res?.data?.error ?? 'Something went wrong.';
}

const PAGE_SIZE = 20;

/**
 * Contracts tab — server-paginated `useCustomerContracts` in an @ui DataTable.
 * Columns mirror the legacy customer-contracts config: Contract No (link),
 * Service, Effective/Expiry dates, Value (VND), Status pill, Role, Direction,
 * Partner (name + code) and Created.
 */
/**
 * Contracts tab — server-paginated contract table with Merge Contracts bulk action.
 * Select the primary contract + additional contracts to absorb, then merge.
 * The first selected contract is treated as the primary (contract_id).
 * USER runs the live merge mutation.
 */
export function ContractsTab({ id }: { id: number }) {
  const [page, setPage] = useState(1);
  const [selection, setSelection] = useState<Record<string, boolean>>({});
  const [mergeOpen, setMergeOpen] = useState(false);
  const { data, isLoading, isError, refetch } = useCustomerContracts(id, {
    page,
    page_size: PAGE_SIZE,
  });
  const merge = useMergeContracts(id);

  const rows = data?.results ?? [];
  const total = data?.pagination?.total ?? rows.length;
  const pageCount = data?.pagination?.total_pages ?? 1;

  const selectedIds = rows
    .filter((r) => selection[String(r.contract_id)])
    .map((r) => r.contract_id);
  const hasSelection = selectedIds.length >= 2;
  // First selected contract is the merge target (primary); rest are absorbed.
  const [primaryId, ...mergeIds] = selectedIds;

  const submitMerge = () => {
    if (!primaryId || mergeIds.length === 0) return;
    merge.mutate(
      { contract_id: primaryId, merge_contract_ids: mergeIds },
      {
        onSuccess: () => {
          setMergeOpen(false);
          setSelection({});
          merge.reset();
        },
      },
    );
  };

  const columns = useMemo<ColumnDef<ContractRow, unknown>[]>(
    () => [
      {
        id: 'contract_number_id',
        header: 'Contract No',
        cell: ({ row }) => (
          <TableLink to={`/contracts/${row.original.contract_id}`}>
            {row.original.contract_number_id || `#${row.original.contract_id}`}
          </TableLink>
        ),
      },
      {
        id: 'service',
        header: 'Service',
        cell: ({ row }) => {
          const { service_name, service_id } = row.original;
          if (!service_id) return '-';
          return (
            <EntityLink
              to={`/services/${service_id}`}
              id={service_id}
              name={service_name}
            />
          );
        },
      },
      {
        id: 'contract_effective_date',
        header: 'Effective',
        cell: ({ row }) => formatDate(row.original.contract_effective_date),
      },
      {
        id: 'contract_expiry_date',
        header: 'Expiry',
        cell: ({ row }) => formatDate(row.original.contract_expiry_date),
      },
      {
        id: 'contract_value',
        header: () => <div style={{ textAlign: 'right' }}>Value</div>,
        cell: ({ row }) => (
          <div style={{ textAlign: 'right' }}>{formatMoney(row.original.contract_value)}</div>
        ),
      },
      {
        id: 'contract_status',
        header: 'Status',
        cell: ({ row }) => (
          <StatusPill tone={contractTone(row.original.contract_status)}>
            {humanize(row.original.contract_status)}
          </StatusPill>
        ),
      },
      {
        id: 'contract_role',
        header: 'Role',
        cell: ({ row }) => humanize(row.original.contract_role),
      },
      {
        id: 'contract_direction',
        header: 'Direction',
        cell: ({ row }) => humanize(row.original.contract_direction),
      },
      {
        id: 'partner',
        header: 'Partner',
        cell: ({ row }) => {
          const { partner_name, partner_code } = row.original;
          if (!partner_name && !partner_code) return '-';
          return (
            <div style={{ display: 'grid', minWidth: 0 }}>
              <span style={{ overflowWrap: 'anywhere' }}>{partner_name || '-'}</span>
              {partner_code ? (
                <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
                  {partner_code}
                </span>
              ) : null}
            </div>
          );
        },
      },
      {
        id: 'created_at',
        header: 'Created',
        cell: ({ row }) => formatDate(row.original.created_at),
      },
    ],
    [],
  );

  if (isError) return <TabError title="Failed to load contracts" onRetry={() => refetch()} />;
  if (isLoading) return <TabLoading />;

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* Bulk action bar — shown when ≥2 contracts are selected */}
      {hasSelection ? (
        <Toolbar
          style={{ marginBottom: 0 }}
          start={
            <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
              {selectedIds.length} contract{selectedIds.length !== 1 ? 's' : ''} selected
            </span>
          }
          end={
            <Button
              variant="secondary"
              startIcon={<GitMerge size={15} />}
              onClick={() => setMergeOpen(true)}
            >
              Merge Contracts
            </Button>
          }
        />
      ) : null}

      <DataTable
        columns={columns}
        data={rows}
        enableSorting={false}
        enableSelection
        rowSelection={selection}
        onRowSelectionChange={setSelection}
        getRowId={(r) => String(r.contract_id)}
        emptyState={<EmptyState title="No contracts" description="This customer has no contracts yet." />}
        footer={(() => {
          const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);
          return (
            <Pagination page={page} pageCount={view.pageCount} onPageChange={setPage} summary={view.summary} />
          );
        })()}
      />

      <ConfirmDialog
        open={mergeOpen}
        onOpenChange={(o) => { if (!merge.isPending) { setMergeOpen(o); if (!o) merge.reset(); } }}
        title="Merge Contracts"
        description={`Merge ${mergeIds.length} contract${mergeIds.length !== 1 ? 's' : ''} into contract #${primaryId}? The merged contracts will be absorbed and removed.`}
        confirmLabel="Merge"
        loading={merge.isPending}
        onConfirm={submitMerge}
      >
        {merge.isError ? (
          <Alert tone="danger" title="Could not merge contracts">{errMsg(merge.error)}</Alert>
        ) : null}
      </ConfirmDialog>
    </div>
  );
}
